import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import GIInterventionPointPicker from './GIInterventionPointPicker';
import Toggle from '../ui/Toggle';

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];
const getLookupMap = (items = [], key = 'Id') => new Map(items.map((item) => [item[key], item]));
const ANIMATED_STATE_ID = 2;

const GIInterventionPointDaySchedule = ({ fetchWithAuth, layout = 'mobile' }) => {
  const isDesktop = layout === 'desktop';
  const [selectedIP, setSelectedIP] = useState(null);
  const [date, setDate] = useState(() => formatYMD(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animatedOnly, setAnimatedOnly] = useState(true);

  const prevDay = () => setDate(formatYMD(new Date(new Date(date).getTime() - 86400000)));
  const nextDay = () => setDate(formatYMD(new Date(new Date(date).getTime() + 86400000)));
  const today = () => setDate(formatYMD(new Date()));

  useEffect(() => {
    const load = async () => {
      if (!selectedIP) return;
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/api/dwh/gi/planning?startDate=${date}&endDate=${date}&interventionPointId=${selectedIP.value}`;
        const resp = await fetchWithAuth(url);
        if (!resp.ok) return setData({ planning: [] });
        const json = await resp.json();
        setData(json);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedIP, date, fetchWithAuth]);

  const summary = useMemo(() => {
    if (!data?.planning?.length) return { promoterCount: 0, clientCount: 0 };
    const promoters = new Set();
    const clients = new Set();
    data.planning.forEach((p) => {
      const visibleTimeslots = (p.Timeslots || []).filter((ts) => (
        !animatedOnly || (ts.StateId ?? p.StateId) === ANIMATED_STATE_ID
      ));

      visibleTimeslots.forEach((ts) => {
        if (ts.PromoterId) promoters.add(ts.PromoterId);
      });

      if (visibleTimeslots.length > 0 && p.ClientId) clients.add(p.ClientId);
    });

    return { promoterCount: promoters.size, clientCount: clients.size };
  }, [animatedOnly, data]);

  const groups = useMemo(() => {
    if (!data?.planning?.length) return [];

    const byClientEntity = new Map();
    const promotersById = getLookupMap(data.promoters);
    const campaignsById = getLookupMap(data.campaigns);

    data.planning.forEach((p) => {
      const groupKey = `${p.ClientId || 'null'}|${p.EntityId || 'null'}`;
      if (!byClientEntity.has(groupKey)) {
        byClientEntity.set(groupKey, {
          clientId: p.ClientId,
          client: p.Client,
          entityId: p.EntityId,
          entity: p.Entity,
          company: p.Company,
          promoters: new Map()
        });
      }

      const group = byClientEntity.get(groupKey);
      const fallbackState = p.State || 'Autre';
      const campaignName = campaignsById.get(p.CampaignId)?.Name || '';

      (p.Timeslots || []).forEach((ts) => {
        if (animatedOnly && (ts.StateId ?? p.StateId) !== ANIMATED_STATE_ID) {
          return;
        }

        const promoterId = ts.PromoterId || null;
        const promoterKey = promoterId || 'unknown';

        if (!group.promoters.has(promoterKey)) {
          group.promoters.set(promoterKey, {
            promoterId,
            promoter: promotersById.get(promoterId)?.Name || 'Inconnu',
            slots: []
          });
        }

        const promoter = group.promoters.get(promoterKey);
        promoter.slots.push({
          start: ts.StartTime || null,
          end: ts.EndTime || null,
          campaign: campaignName,
          client: p.Client,
          company: p.Company,
          state: ts.State || fallbackState,
          stateId: ts.StateId ?? p.StateId ?? null
        });
      });
    });

    return Array.from(byClientEntity.values())
      .map((group) => {
        const promoters = Array.from(group.promoters.values())
          .map((promoter) => ({
            ...promoter,
            slots: promoter.slots.sort((a, b) => (a.start || '').localeCompare(b.start || ''))
          }))
          .filter((promoter) => promoter.slots.length > 0);

        const firstSlotStart = (promoter) => promoter.slots[0]?.start || '';
        return {
          ...group,
          promoters: promoters.sort((a, b) => firstSlotStart(a).localeCompare(firstSlotStart(b)))
        };
      })
      .filter((group) => group.promoters.length > 0)
      .sort((a, b) => (
        (a.client || '').localeCompare(b.client || '') ||
        (a.entity || '').localeCompare(b.entity || '')
      ));
  }, [animatedOnly, data]);

  return (
    <div className={`space-y-3 ${isDesktop ? 'max-w-6xl mx-auto' : ''}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border border-gray-200 dark:border-gray-700">
        <div className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">Point d'intervention</div>
        <GIInterventionPointPicker
          value={selectedIP}
          onChange={setSelectedIP}
          fetchWithAuth={fetchWithAuth}
          className={isDesktop ? 'max-w-3xl' : ''}
        />
        <div className={`mt-3 flex ${isDesktop ? 'flex-wrap' : ''} items-center gap-2`}>
          <button
            onClick={prevDay}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            J-1
          </button>
          <button
            onClick={today}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Aujourd'hui
          </button>
          <button
            onClick={nextDay}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            J+1
          </button>
          <div className="ml-auto text-sm text-gray-600 dark:text-gray-300">
            {new Date(date).toLocaleDateString('fr-FR')}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border border-gray-200 dark:border-gray-700">
        {!selectedIP ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Sélectionnez un point d'intervention pour voir les promoteurs du jour.
          </div>
        ) : loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Chargement…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-xs font-medium">
                  {summary.promoterCount} promoteur{summary.promoterCount > 1 ? 's' : ''}
                </div>
                <div className="bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded-full text-xs font-medium">
                  {summary.clientCount} client{summary.clientCount > 1 ? 's' : ''}
                </div>
              </div>
              <Toggle
                checked={animatedOnly}
                onChange={(event) => setAnimatedOnly(event.target.checked)}
                label="Animé only"
                labelPosition="right"
              />
            </div>

            {groups.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {animatedOnly
                  ? "Aucun promoteur animé trouvé pour cette date."
                  : "Aucun promoteur trouvé pour cette date."}
              </div>
            ) : (
              <div className={isDesktop ? 'grid grid-cols-1 xl:grid-cols-2 gap-4' : 'space-y-4'}>
                {groups.map((group, gIdx) => (
                  <div key={gIdx} className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {group.client}
                      </div>
                      {group.company && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{group.entity}</div>
                      )}
                    </div>
                    <div className="mt-2 space-y-3">
                      {group.promoters.map((row, idx) => (
                        <div key={idx}>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{row.promoter}</div>
                          <div className="mt-1 space-y-1">
                            {row.slots.map((slot, slotIdx) => (
                              <div key={slotIdx} className="text-sm text-gray-700 dark:text-gray-300 flex items-center justify-between">
                                <div>
                                  <span className="font-mono mr-2 text-gray-800 dark:text-gray-200">
                                    {slot.start?.substring(0, 5)} - {slot.end?.substring(0, 5)}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{slot.state || 'Autre'}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GIInterventionPointDaySchedule;
